import { FC, useState } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { BasicRow } from 'modules/finance/components/FinancialWorkspace/components/Table/table/Row/BasicRow'
import { CollapsableRow } from 'modules/finance/components/FinancialWorkspace/components/Table/table/Row/CollapsableRow'
import { regroupedItemsByCategory } from 'modules/finance/components/FinancialWorkspace/lib/regroupedItemsByCategory'
import {
  LazyChildren,
  PaginatedTableItem,
  SUMMARIZED_VALUES,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'

import { ChildrenLoading } from './ChildrenLoading'

interface RowsProps {
  level: number
  expanded?: boolean
  headerCount: number
  isLastRow?: boolean
  currentPageCount: number
  loadingChildren?: boolean
  withLazyChildren?: boolean
  items: PaginatedTableItem[]
  groupBy: InsightTableGroupBy
  parentHasItemWithChildren?: boolean
  onExpand?: (expanded: boolean) => void
  tableHeadRef?: React.RefObject<HTMLTableSectionElement>
}

export const NestedRows: FC<RowsProps> = ({
  items,
  level,
  groupBy,
  expanded,
  onExpand,
  isLastRow,
  headerCount,
  tableHeadRef,
  loadingChildren,
  withLazyChildren,
  currentPageCount,
  parentHasItemWithChildren,
}) => {
  const sortedItems = [...items].sort((a, _b) => {
    const priorityA = a.priority

    if (priorityA === 'high') return -1

    return 0
  })

  const hasCategoryLabel = sortedItems.some((item) => item.categoryLabel)
  const firstLevel = level === 0

  if (hasCategoryLabel) {
    const categorizedItems = regroupedItemsByCategory(sortedItems)

    return (
      <NestedRows
        items={categorizedItems}
        headerCount={headerCount}
        level={level}
        currentPageCount={categorizedItems.length}
        isLastRow={isLastRow}
        expanded={expanded}
        withLazyChildren={withLazyChildren}
        loadingChildren={loadingChildren}
        tableHeadRef={tableHeadRef}
        groupBy={groupBy}
      />
    )
  }

  const hasSiblingWithChildren =
    parentHasItemWithChildren ||
    sortedItems.some((item) => {
      return !!(
        withLazyChildren ||
        item.children?.length ||
        item.customChildren?.length ||
        (!!item.onLazyChildren &&
          (item.data.some((i) => Number(i.totalAmountInCents) > 0) ||
            item.comparisonData?.some((i) => Number(i.totalAmountInCents) > 0)))
      )
    })

  return (
    <>
      {sortedItems.map((item, index) => (
        <Row
          headerCount={headerCount}
          key={item.id}
          item={item}
          level={level}
          currentPageCount={currentPageCount}
          isLastRow={firstLevel && index === currentPageCount - 1}
          expanded={expanded}
          withLazyChildren={withLazyChildren}
          loadingChildren={loadingChildren}
          onExpand={onExpand}
          tableHeadRef={tableHeadRef}
          groupBy={groupBy}
          hasSiblingWithChildren={hasSiblingWithChildren}
        />
      ))}
    </>
  )
}

interface PaginatedRowProps {
  headerCount: number
  level: number
  currentPageCount: number
  item: PaginatedTableItem
  onLazyChildren: LazyChildren
  groupBy: InsightTableGroupBy
  hasSiblingWithChildren?: boolean
}

const PaginatedRow = ({
  item,
  level,
  groupBy,
  headerCount,
  onLazyChildren,
  currentPageCount,
  hasSiblingWithChildren,
}: PaginatedRowProps) => {
  const [children, setChildren] = useState<PaginatedTableItem[]>([])
  const [loading, setLoading] = useState(false)

  const onExpand = async () => {
    setLoading(true)

    const { data: newData } = await onLazyChildren(item.id)

    setChildren(newData)

    setLoading(false)
  }

  const formattedItem = useUnsafeMemo(() => {
    return [{ ...item, onLazyChildren: undefined, children: children }]
  }, [item, children])

  const hasResults =
    item.data.some((i) => Number(i.totalAmountInCents) > 0) ||
    item.comparisonData?.some((i) => Number(i.totalAmountInCents) > 0)

  return (
    <NestedRows
      key={item.id}
      level={level}
      expanded={false}
      groupBy={groupBy}
      onExpand={onExpand}
      items={formattedItem}
      headerCount={headerCount}
      currentPageCount={currentPageCount}
      withLazyChildren={hasResults || undefined}
      parentHasItemWithChildren={hasSiblingWithChildren}
      loadingChildren={hasResults ? loading : undefined}
    />
  )
}

interface RowProps {
  item: PaginatedTableItem
  isLastRow: boolean
  headerCount: number
  level: number
  tooltip?: string
  currentPageCount: number
  expanded?: boolean
  withLazyChildren?: boolean
  loadingChildren?: boolean
  onExpand?: (expanded: boolean) => void
  tableHeadRef?: React.RefObject<HTMLTableSectionElement>
  groupBy: InsightTableGroupBy
  hasSiblingWithChildren?: boolean
}

const Row: FC<RowProps> = ({
  item,
  isLastRow,
  level,
  currentPageCount,
  headerCount,
  expanded,
  withLazyChildren,
  loadingChildren,
  onExpand,
  tableHeadRef,
  groupBy,
  hasSiblingWithChildren,
}) => {
  const [isOpen, setOpen] = useState(expanded ?? item.expanded ?? true)
  const [hasItemWithChildren, setHasItemWithChildren] = useState(false)

  useUnsafeEffect(() => {
    if (item.loading) {
      setOpen(false)
    }
  }, [item.loading])

  if (item.customRow && item.customRow.visible) {
    return (
      <item.customRow.component
        key={item.id}
        level={level}
        headerLength={headerCount}
        tableHeadRef={tableHeadRef}
      />
    )
  }

  const children = item.children
  const onLazyChildren = item.onLazyChildren

  const hasChildren =
    children?.length || item.customChildren?.length || !!onLazyChildren || !!withLazyChildren
  const showImage = level === 0 && groupBy === 'employee'

  if (!hasChildren) {
    return (
      <BasicRow
        level={level}
        data={item.data}
        grey={item.grey}
        title={item.label}
        active={item.active}
        italic={item.italic}
        dashed={item.dashed}
        showImage={showImage}
        tooltip={item.tooltip}
        loading={item.loading}
        subLabel={item.subLabel}
        imageUrl={item.imageUrl}
        headerCount={headerCount}
        isUngrouped={item.isUngrouped}
        isSummarized={item.isSummarized}
        titleWrapper={item.titleWrapper}
        comparisonData={item.comparisonData}
        hasSiblingWithChildren={hasSiblingWithChildren}
      />
    )
  }

  if (onLazyChildren) {
    return (
      <PaginatedRow
        item={item}
        level={level}
        key={item.id}
        groupBy={groupBy}
        headerCount={headerCount}
        onLazyChildren={onLazyChildren}
        currentPageCount={currentPageCount}
        hasSiblingWithChildren={hasSiblingWithChildren}
      />
    )
  }

  const sortedChildren = [...(children ?? [])].sort(
    (a, b) => a?.label?.localeCompare(b?.label ?? '') ?? 0
  )

  const hasCategoryLabel = sortedChildren.some((i) => i.categoryLabel)
  const hasSummarizedValues = sortedChildren.some((i) => i.category === SUMMARIZED_VALUES)
  const showBlank = hasCategoryLabel && hasSummarizedValues

  return (
    <>
      {!isOpen && (
        <CollapsableRow
          item={item}
          level={level}
          isOpen={false}
          toggleRow={() => {
            setOpen(true)
            onExpand?.(true)
          }}
          showImage={showImage}
          isLastRow={isLastRow}
          showBlank={showBlank}
          headerCount={headerCount}
        />
      )}

      {isOpen && (
        <>
          <CollapsableRow
            isOpen
            item={item}
            level={level}
            toggleRow={() => {
              setOpen(false)
              onExpand?.(false)
            }}
            showImage={showImage}
            isLastRow={isLastRow}
            showBlank={showBlank}
            headerCount={headerCount}
          />
          {hasCategoryLabel ? (
            <NestedRows
              level={level + 1}
              isLastRow={isLastRow}
              items={sortedChildren}
              currentPageCount={currentPageCount}
              headerCount={headerCount}
              groupBy={groupBy}
            />
          ) : (
            <>
              {loadingChildren ? (
                <ChildrenLoading level={level + 1} />
              ) : (
                sortedChildren.map((data, index) => (
                  <BasicRow
                    data={data.data}
                    level={level + 1}
                    title={data.label}
                    subLabel={item.subLabel}
                    headerCount={headerCount}
                    key={`${item.id}-${index}`}
                    grey={data.grey || item.grey}
                    isUngrouped={item.isUngrouped}
                    isSummarized={item.isSummarized}
                    italic={data.italic || item.italic}
                    dashed={data.dashed || item.dashed}
                    comparisonData={data.comparisonData}
                    loading={item.loading || loadingChildren}
                  />
                ))
              )}
            </>
          )}
          {item.customChildren?.map((data, index) => (
            <data.component
              id={item.id}
              groupBy={groupBy}
              level={level + 1}
              headerLength={headerCount}
              key={`${item.id}-${index}`}
              tableHeadRef={tableHeadRef}
              parentHasItemWithChildren={hasItemWithChildren}
              setParentHasItemWithChildren={setHasItemWithChildren}
            />
          ))}
        </>
      )}
    </>
  )
}
